// AI API Service - Connects to real AI APIs using Firebase-stored user credentials
import toast from 'react-hot-toast';
import FirebaseUserDataService from './firebaseUserData';
import UnsubscribeService from './unsubscribeService';
import IntegrationService from './integrationService';

export class AIService {
  // Get stored API keys from Firebase - checks BOTH locations
  static async getStoredAPIKeys(userId, tenantId = null) {
    try {
      // First try the userData location (old method)
      const userDataKeys = await FirebaseUserDataService.getAPIKeys(userId);
      
      // Also check IntegrationService location (new method)
      let integrationKeys = [];
      const effectiveTenantId = tenantId || userId;
      
      // Check for each AI provider in IntegrationService
      const aiProviders = ['openai', 'anthropic', 'gemini', 'groq'];
      for (const provider of aiProviders) {
        try {
          const result = await IntegrationService.getIntegrationCredentials(effectiveTenantId, provider);
          if (result.success && result.data && result.data.apiKey && result.data.status === 'connected') {
            integrationKeys.push({
              id: `integration_${provider}`,
              name: provider.charAt(0).toUpperCase() + provider.slice(1),
              service: provider,
              key: result.data.apiKey,
              status: 'active',
              source: 'integrationService'
            });
          }
        } catch (e) {
          // Provider not found, continue
        }
      }
      
      // Merge both sources, preferring IntegrationService (newer)
      const allKeys = [...integrationKeys];
      
      // Add userData keys that aren't duplicates
      for (const key of userDataKeys) {
        const exists = allKeys.some(k => k.service.toLowerCase() === key.service.toLowerCase());
        if (!exists) {
          allKeys.push(key);
        }
      }
      
      return allKeys;
    } catch (error) {
      console.error('Error loading API keys:', error);
      return [];
    }
  }

  // Get the first active API key for a specific service
  static async getAPIKey(userId, serviceName, tenantId = null) {
    const apiKeys = await this.getStoredAPIKeys(userId, tenantId);
    const key = apiKeys.find(k => 
      k.service.toLowerCase().includes(serviceName.toLowerCase()) && 
      k.status === 'active'
    );
    return key?.key || null;
  }

  // Generate email content using OpenAI GPT-4
  static async generateWithOpenAI(userId, prompt, campaignData, apiKey = null) {
    // Use provided key or fetch from storage
    const key = apiKey || await this.getAPIKey(userId, 'openai');
    if (!key) {
      throw new Error('No active OpenAI API key found. Please add one in API Keys & Integrations.');
    }

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are an expert email marketing copywriter. Create compelling, personalized email content that drives engagement and conversions. Keep emails professional, engaging, and action-oriented. Avoid spam triggers: limit exclamation points to 1-2 per email, use natural conversational language, avoid excessive promotional phrases, and focus on genuine value.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 1000,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'OpenAI API request failed');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('OpenAI API Error:', error);
      throw error;
    }
  }

  // Generate email content using Anthropic Claude
  static async generateWithClaude(userId, prompt, campaignData, apiKey = null) {
    const key = apiKey || await this.getAPIKey(userId, 'anthropic');
    if (!key) {
      throw new Error('No active Anthropic API key found. Please add one in API Keys & Integrations.');
    }

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': key,
          'Content-Type': 'application/json',
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-sonnet-20240229',
          max_tokens: 1000,
          messages: [
            {
              role: 'user',
              content: `You are an expert email marketing copywriter. Create compelling, personalized email content that drives engagement and conversions. Keep emails professional, engaging, and action-oriented. Avoid spam triggers: limit exclamation points to 1-2 per email, use natural conversational language, avoid excessive promotional phrases, and focus on genuine value.\n\n${prompt}`
            }
          ]
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Anthropic API request failed');
      }

      const data = await response.json();
      return data.content[0].text;
    } catch (error) {
      console.error('Anthropic API Error:', error);
      throw error;
    }
  }

  // Generate email content using Google Gemini
  static async generateWithGemini(userId, prompt, campaignData, apiKey = null) {
    const key = apiKey || await this.getAPIKey(userId, 'gemini');
    if (!key) {
      throw new Error('No active Google Gemini API key found. Please add one in API Keys & Integrations.');
    }

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${key}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `You are an expert email marketing copywriter. Create compelling, personalized email content that drives engagement and conversions. Keep emails professional, engaging, and action-oriented. Avoid spam triggers: limit exclamation points to 1-2 per email, use natural conversational language, avoid excessive promotional phrases, and focus on genuine value.\n\n${prompt}`
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 1000,
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Google Gemini API request failed');
      }

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    } catch (error) {
      console.error('Gemini API Error:', error);
      throw error;
    }
  }

  // Generate email content using Groq AI
  static async generateWithGroq(userId, prompt, campaignData, apiKey = null) {
    const key = apiKey || await this.getAPIKey(userId, 'groq');
    if (!key) {
      throw new Error('No active Groq API key found. Please add one in API Keys & Integrations.');
    }

    try {
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'openai/gpt-oss-20b',
          messages: [
            {
              role: 'system',
              content: 'You are an expert email marketing copywriter. Create compelling, personalized email content that drives engagement and conversions. Keep emails professional, engaging, and action-oriented. Avoid spam triggers: limit exclamation points to 1-2 per email, use natural conversational language, avoid excessive promotional phrases, and focus on genuine value.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 1000,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Groq API request failed');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Groq API Error:', error);
      throw error;
    }
  }

  // Main function to generate email content with AI
  static async generateEmailContent(userId, campaignData, preferredProvider = null, tenantId = null, recipientEmail = null, businessInfo = {}, senderInfo = {}) {
    const { name, type, targetAudience, subject, additionalPrompt, callToActionText, callToActionUrl } = campaignData;
    
    const prompt = `
You are a skilled email marketing copywriter who creates engaging, mobile-friendly emails that respect recipients' time.

Campaign Name: ${name}
Campaign Type: ${type}
Target Audience: ${targetAudience}

${additionalPrompt ? `Additional requirements:
${additionalPrompt}

` : ''}${callToActionText && callToActionUrl ? `Call-to-action details:
- Include a button-style link for: "${callToActionText}"
- Format the button exactly like this:
  <p style="margin-bottom: 16px; margin-top: 20px; text-align: center;"><a href="${callToActionUrl}" style="background-color: #0066cc; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">${callToActionText}</a></p>

` : ''}Email formatting guidelines:
- Keep paragraphs short: 2-3 sentences maximum per paragraph
- Break long ideas into multiple short paragraphs for easy scanning
- Use line breaks to create visual breathing room
- Each paragraph should be easily readable on mobile devices

Formatting rules:
- Start with a warm greeting like "Hi {firstName}," or "Dear {firstName},"
- NEVER include "Subject Line:" or "Subject:" in the email body
- NEVER use markdown formatting (**, *, -, #, [], etc.)
- Use <p style="margin-bottom: 16px;">content</p> for each paragraph
- Use <strong>bold</strong> sparingly for key phrases only
- For bullet lists: <p style="margin-bottom: 8px;">• Item here</p>
- Keep bullet items to one line each

Content structure:
1. Greeting + opening hook (1-2 sentences)
2. Context and story - break into 2-3 short paragraphs
3. Key benefits - use 3-4 bullet points maximum
4. Social proof or gentle encouragement (1 short paragraph)
5. Call-to-action (final paragraph)

Anti-spam guidelines:
- Limit exclamation points to 1-2 per email maximum
- Use natural, conversational language
- Avoid excessive promotional phrases like "revolutionary," "amazing," "don't miss out"
- Focus on genuine value and benefits
- Keep tone helpful and informative, not salesy

Write the email now. Focus on being helpful and valuable to the recipient.
    `;

    // Use tenantId for key lookup
    const effectiveTenantId = tenantId || userId;
    const apiKeys = await this.getStoredAPIKeys(userId, effectiveTenantId);
    const activeKeys = apiKeys.filter(k => k.status === 'active');

    if (activeKeys.length === 0) {
      throw new Error('No active AI API keys found. Please add API keys in the API Keys & Integrations section.');
    }

    // Try preferred provider first, then fallback to available providers
    const providers = [
      { name: 'openai', func: this.generateWithOpenAI },
      { name: 'anthropic', func: this.generateWithClaude },
      { name: 'gemini', func: this.generateWithGemini },
      { name: 'groq', func: this.generateWithGroq }
    ];

    let generatedContent = '';

    // If preferred provider is specified, try it first
    if (preferredProvider) {
      const provider = providers.find(p => p.name === preferredProvider.toLowerCase());
      const providerKey = activeKeys.find(k => k.service.toLowerCase().includes(provider?.name || ''));
      if (provider && providerKey) {
        try {
          generatedContent = await provider.func.call(this, userId, prompt, campaignData, providerKey.key);
          toast.success(`Email generated successfully with ${provider.name.toUpperCase()}!`);
        } catch (error) {
          console.warn(`${preferredProvider} failed, trying fallback providers:`, error.message);
        }
      }
    }

    // Try each available provider if no content generated yet
    if (!generatedContent) {
      for (const provider of providers) {
        const providerKey = activeKeys.find(k => k.service.toLowerCase().includes(provider.name));
        if (providerKey) {
          try {
            generatedContent = await provider.func.call(this, userId, prompt, campaignData, providerKey.key);
            toast.success(`Email generated successfully with ${provider.name.toUpperCase()}!`);
            break;
          } catch (error) {
            console.warn(`${provider.name} failed:`, error.message);
            continue;
          }
        }
      }
    }

    if (!generatedContent) {
      throw new Error('All AI providers failed. Please check your API keys and try again.');
    }

    // Clean up excessive line spacing and formatting issues
    generatedContent = generatedContent
      .replace(/\n\s*\n\s*\n/g, '\n\n')  // Replace triple+ line breaks with double
      .replace(/(<\/p>)\s*\n+\s*(<p>)/g, '$1\n$2')  // Clean spacing between paragraphs
      .replace(/\n\s+/g, '\n')  // Remove leading whitespace on lines
      .trim();

    // Add clear visual separation before footer to preserve CTA prominence
    if (tenantId && recipientEmail) {
      const campaignId = `campaign_${Date.now()}`;
      // Add visual break to separate main content (with CTA) from footer
      generatedContent += `\n\n<div style="margin: 40px 0; border-top: 2px solid #e5e7eb; opacity: 0.3;"></div>\n`;
      const unsubscribeFooter = UnsubscribeService.generateUnsubscribeFooter(
        tenantId, 
        recipientEmail, 
        campaignId,
        businessInfo
      );
      generatedContent += unsubscribeFooter;
    } else if (Object.keys(senderInfo).length > 0) {
      // Add visual break before signature
      generatedContent += `\n\n<div style="margin: 40px 0; border-top: 2px solid #e5e7eb; opacity: 0.3;"></div>\n`;
      const signature = UnsubscribeService.generateEmailSignature(senderInfo);
      generatedContent += signature;
    }

    return generatedContent;
  }

  static buildWorkflowSystemPrompt() {
    return `You are a workflow automation expert. Convert natural language descriptions into structured workflow definitions.

AVAILABLE NODE TYPES:
TRIGGERS (workflow starts):
- lead_added: When new lead is added
- email_opened: When email is opened
- email_clicked: When link is clicked
- email_replied: When lead replies to email
- form_submitted: When form is submitted
- schedule: Runs on a schedule (time-based)
- tag_added: When a tag is added to a contact

ACTIONS (do something):
- send_email: Send an email
- send_sms: Send SMS message
- add_to_list: Add contact to a list
- validate_email: Validate email address
- create_deal: Create a sales deal
- book_appointment: Schedule a meeting
- send_notification: Send internal notification
- webhook: Call external API

LOGIC (decisions/delays):
- if_else: Branch based on condition
- delay: Wait for specified time
- end: Stop workflow

DATA (modify contact data):
- add_tag: Add a tag to contact
- update_field: Update contact field
- update_score: Update lead score
- save_to_db: Save data to database
- remove_tag: Remove a tag from contact

Return ONLY a JSON object (no markdown, no explanations) with this exact structure:
{
  "workflowName": "Brief workflow name",
  "description": "One sentence describing what this workflow does",
  "nodes": [
    {
      "id": "unique_id",
      "type": "triggerNode|actionNode|conditionNode|delayNode|dataNode|endNode",
      "label": "Display name",
      "config": {
        "triggerType": "if trigger node",
        "actionType": "if action node",
        "dataOperation": "if data node",
        "subject": "if email",
        "body": "if email",
        "condition": "if condition node",
        "delay": "if delay node (e.g. '2 days')"
      }
    }
  ],
  "connections": [
    {"from": "node_id", "to": "node_id"}
  ]
}`;
  }

  static async generateWorkflowWithOpenAI(userId, prompt, apiKey) {
    const key = apiKey || await this.getAPIKey(userId, 'openai');
    if (!key) {
      throw new Error('No active OpenAI API key found. Please add one in API Keys & Integrations.');
    }

    const systemPrompt = this.buildWorkflowSystemPrompt();
    const requestWorkflow = async (model) => {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${key}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: prompt }
          ],
          max_tokens: 2000,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'OpenAI API request failed');
      }

      const data = await response.json();
      return data.choices[0].message.content.trim();
    };

    try {
      return await requestWorkflow('gpt-4');
    } catch (error) {
      const message = error.message || '';
      const needFallback = message.toLowerCase().includes('gpt-4') || message.toLowerCase().includes('model') || message.toLowerCase().includes('access') || message.toLowerCase().includes('not available');
      if (needFallback) {
        try {
          return await requestWorkflow('gpt-3.5-turbo');
        } catch (fallbackError) {
          throw new Error(fallbackError.message || 'OpenAI API request failed on fallback model');
        }
      }
      throw error;
    }
  }

  static parseAIWorkflowResponse(rawText) {
    if (!rawText || typeof rawText !== 'string') {
      throw new Error('AI response is empty or invalid');
    }

    let content = rawText.trim();

    // Remove common markdown fences and extra text wrappers
    if (content.startsWith('```')) {
      const fenceEnd = content.lastIndexOf('```');
      if (fenceEnd > 0) {
        content = content.slice(content.indexOf('\n') + 1, fenceEnd).trim();
      }
    }

    // Extract JSON object from the response if there is surrounding text
    const firstBrace = content.indexOf('{');
    const lastBrace = content.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const candidate = content.slice(firstBrace, lastBrace + 1);
      try {
        return JSON.parse(candidate);
      } catch (error) {
        // fall through to parse raw content below
      }
    }

    return JSON.parse(content);
  }

  static async generateWorkflowWithClaude(userId, prompt, apiKey) {
    const key = apiKey || await this.getAPIKey(userId, 'anthropic');
    if (!key) {
      throw new Error('No active Anthropic API key found. Please add one in API Keys & Integrations.');
    }

    const systemPrompt = this.buildWorkflowSystemPrompt();
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'x-api-key': key,
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-3-sonnet-20240229',
        max_tokens: 2000,
        messages: [
          {
            role: 'user',
            content: `${systemPrompt}\n\n${prompt}`
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Anthropic workflow API request failed');
    }

    const data = await response.json();
    return data.content[0].text.trim();
  }

  static async generateWorkflowWithGemini(userId, prompt, apiKey) {
    const key = apiKey || await this.getAPIKey(userId, 'gemini');
    if (!key) {
      throw new Error('No active Google Gemini API key found. Please add one in API Keys & Integrations.');
    }

    const systemPrompt = this.buildWorkflowSystemPrompt();
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${key}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `${systemPrompt}\n\n${prompt}`
          }]
        }],
        generationConfig: {
          temperature: 0.3,
          maxOutputTokens: 1200
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Google Gemini workflow API request failed');
    }

    const data = await response.json();
    return data.candidates[0].content.parts[0].text.trim();
  }

  static async generateWorkflowWithGroq(userId, prompt, apiKey) {
    const key = apiKey || await this.getAPIKey(userId, 'groq');
    if (!key) {
      throw new Error('No active Groq API key found. Please add one in API Keys & Integrations.');
    }

    const systemPrompt = this.buildWorkflowSystemPrompt();
    const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${key}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'openai/gpt-oss-20b',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        max_tokens: 1200,
        temperature: 0.3
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Groq workflow API request failed');
    }

    const data = await response.json();
    return data.choices[0].message.content.trim();
  }

  // Generate visual workflow from natural language prompt
  static async generateWorkflowFromPrompt(userId, prompt, tenantId = null, preferredProvider = null) {
    const effectiveTenantId = tenantId || userId;
    const apiKeys = await this.getStoredAPIKeys(userId, effectiveTenantId);
    const activeKeys = apiKeys.filter(k => k.status === 'active');

    if (activeKeys.length === 0) {
      throw new Error('No active AI API keys found. Please add API keys in API Keys & Integrations.');
    }

    const providers = [
      { name: 'openai', func: this.generateWorkflowWithOpenAI },
      { name: 'anthropic', func: this.generateWorkflowWithClaude },
      { name: 'gemini', func: this.generateWorkflowWithGemini },
      { name: 'groq', func: this.generateWorkflowWithGroq }
    ];

    const errors = [];

    // Try preferred provider first when specified
    if (preferredProvider) {
      const preferred = providers.find(p => p.name === preferredProvider.toLowerCase());
      if (preferred) {
        const providerKey = activeKeys.find(k => k.service.toLowerCase().includes(preferred.name));
        if (providerKey) {
          try {
            const workflowJSON = await preferred.func.call(this, userId, prompt, providerKey.key);
            const workflow = this.parseAIWorkflowResponse(workflowJSON);
            return { success: true, workflow };
          } catch (error) {
            const message = error.message || 'Unknown error';
            console.warn(`${preferredProvider} workflow generation failed, trying fallback providers:`, message);
            errors.push(`${preferred.name}: ${message}`);
          }
        } else {
          errors.push(`${preferred.name}: no active API key found`);
        }
      }
    }

    for (const provider of providers) {
      const providerKey = activeKeys.find(k => k.service.toLowerCase().includes(provider.name));
      if (!providerKey) {
        errors.push(`${provider.name}: no active API key found`);
        continue;
      }

      try {
        const workflowJSON = await provider.func.call(this, userId, prompt, providerKey.key);
        const workflow = this.parseAIWorkflowResponse(workflowJSON);
        return { success: true, workflow };
      } catch (error) {
        const message = error.message || 'Unknown error';
        console.warn(`${provider.name} workflow generation failed:`, message);
        errors.push(`${provider.name}: ${message}`);
        continue;
      }
    }

    return {
      success: false,
      error: 'All AI workflow providers failed. Please check your API keys and try again.',
      details: errors
    };

    return {
      success: false,
      error: 'All AI workflow providers failed. Please check your API keys and try again.'
    };
  }
}

export default AIService;